import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  Suspense,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiAxios as axios } from "../../utils/apiClient.js";
import {
  Container,
  Card,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  Rating,
  Box,
  useMediaQuery,
  useTheme,
  Chip,
  Avatar,
  Typography,
} from "@mui/material";
import { FiMapPin } from "react-icons/fi";
import { Phone, Chat, BookOnline, Close } from "@mui/icons-material";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { motion } from "framer-motion";
import { url } from "../../Constant/TextConstant";
import { useAuth } from "../../utils/AuthContext";
import "./ServiceSearchScreen.css";
import Header from "../Header";
import Footer from "../Footer/Footer";
import "../ServiceProviderDetails/ServiceProviderDetails.css";
import { Helmet } from "react-helmet";
import {
  sendFCMNotification,
  fetchUniqueIds,
} from "../../Services/NotificationServices";
import { useUserLocation } from "../../utils/useUserLocation"; // Import the custom hook
import { encryptData } from "../../utils/encryption";

const ServiceSearchScreen = () => {
  const { searchTerm } = useParams();
  const decodedSearchTerm = decodeURIComponent(searchTerm || "");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { currentUser, currentUserName } = useAuth();

  // Use the custom location hook
  const {
    location: userLocation, // Contains coordinates, address, and details
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

  // State for data loading and management
  const [isLoading, setIsLoading] = useState(true);
  const [serviceData, setServiceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [description, setDescription] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [searchQuery, setSearchQuery] = useState(decodedSearchTerm);

  const scrollRef = useRef();

  // Show snackbar notification
  const showSnackbar = useCallback((message, severity) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Close snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // Fetch service rating
  const fetchRating = useCallback(async (mobile) => {
    try {
      const response = await axios.get(
        `${url.baseAPIUrl}/api/ServiceLikeReviewCall/GetServiceRatingPercentage?ProviderMobile=${mobile}`
      );
      return response.data / 20; // Convert to 5-star scale
    } catch (error) {
      return 0;
    }
  }, []);

  // Fetch service reviews
  const fetchReviews = useCallback(async (mobile) => {
    try {
      const response = await axios.get(
        `${url.baseAPIUrl}/api/ServiceLikeReviewCall/GetReview?ProviderMobile=${mobile}`
      );
      return response.data || [];
    } catch (error) {
      return [];
    }
  }, []);

  // Fetch service subcategories
  const fetchSubCategories = useCallback(async (mobile) => {
    try {
      const response = await axios.get(
        `${url.baseAPIUrl}/api/BindServiceProduct/GetServicesByMobile?providerMobile=${mobile}`
      );
      return response.data.map((item) => ({
        serviceName: item.ServiceName,
        mrp: item.Mrp,
        pricePerAssign: item.PricePerAssign === "ON",
      }));
    } catch (error) {
      return [];
    }
  }, []);

  // Fetch services based on search term
  const fetchServices = useCallback(async () => {
    if (!decodedSearchTerm) {
      setIsLoading(false);
      setServiceData([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(
        `${
          url.baseAPIUrl
        }/api/BindServiceProduct/SearchProductDetails?searchTerm=${encodeURIComponent(
          decodedSearchTerm
        )}&userLatitude=${
          userLocation?.coordinates?.latitude || 0
        }&userLongitude=${userLocation?.coordinates?.longitude || 0}`
      );

      const servicesWithDetails = await Promise.all(
        response.data.map(async (service) => {
          const [rating, reviews, subCategories] = await Promise.all([
            fetchRating(service.ProviderMobile),
            fetchReviews(service.ProviderMobile),
            fetchSubCategories(service.ProviderMobile),
          ]);

          return {
            ...service,
            rating,
            reviews,
            subCategories,
          };
        })
      );

      setServiceData(servicesWithDetails);
      setFilteredData(servicesWithDetails);
    } catch (error) {
      showSnackbar("Failed to load services", "error");
      setServiceData([]);
      setFilteredData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    decodedSearchTerm,
    userLocation?.coordinates?.latitude,
    userLocation?.coordinates?.longitude,
    fetchRating,
    fetchReviews,
    fetchSubCategories,
    showSnackbar,
  ]);

  // Update localStorage when selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Fetch services when location or search term changes
  useEffect(() => {
    if (
      userLocation?.coordinates?.latitude &&
      userLocation?.coordinates?.longitude
    ) {
      fetchServices();
    }
  }, [decodedSearchTerm, userLocation, fetchServices]);

  // Handle service card click
  const handleServiceClick = useCallback(
    (service) => {
      navigate(`/provider-details/${encryptData(service.ProviderMobile)}`, {
        state: { serviceProviderName: service.FullName },
      });
    },
    [navigate]
  );

  // Open booking dialog
  const openBookingDialog = (service) => {
    setSelectedService(service);
    // Automatically select all services that don't require call for price
    const autoSelectedServices =
      service.subCategories
        ?.filter((subCat) => !subCat.pricePerAssign)
        .map((subCat) => subCat.serviceName) || [];
    setSelectedServices(autoSelectedServices);
    setDescription("");
    setBookingDialogOpen(true);
  };

  // Handle service selection for booking
  const handleServiceSelection = (serviceName, isSelected) => {
    setSelectedServices((prev) =>
      isSelected
        ? [...prev, serviceName]
        : prev.filter((name) => name !== serviceName)
    );
  };

  // Calculate total MRP for selected services
  const calculateTotalMRP = () => {
    if (!selectedService) return 0;
    return selectedServices.reduce((total, serviceName) => {
      const service = selectedService.subCategories.find(
        (s) => s.serviceName === serviceName
      );
      return total + (parseFloat(service?.mrp) || 0);
    }, 0);
  };

  // Book selected services
  const bookService = async () => {
    try {
      const bookingData = {
        customerMobile: currentUser,
        customerName: currentUserName,
        providerMobile: selectedService.ProviderMobile,
        serviceNames: selectedServices.join(","),
        description: description || "No additional details provided",
      };

      await axios.post(
        `${url.baseAPIUrl}/api/ServiceBooking/BookService`,
        null,
        {
          params: {
            CustomerName: bookingData.customerName,
            CustomerMobile: bookingData.customerMobile,
            ProviderMobile: bookingData.providerMobile,
            ServiceNames: bookingData.serviceNames,
            Description: bookingData.description,
          },
        }
      );

      setBookingDialogOpen(false);
      const uniqueIds = await fetchUniqueIds(bookingData.providerMobile);
      await sendFCMNotification({
        Tokens: uniqueIds,
        Title: "New Service Booking",
        Body: `A new service booking has been placed by ${
          bookingData.customerName || "Customer"
        }`,
        ScreenRoute: "/serviceBooking",
      });

      const supportUniqueIds = await fetchUniqueIds("9523812734");
      if (supportUniqueIds) {
        await sendFCMNotification({
          Tokens: supportUniqueIds,
          Title: "New Service Booking",
          Body: `A new service booking has been placed by ${
            currentUserName || "Customer"
          }`,
          ScreenRoute: "/serviceBooking",
        });
      }
      showSnackbar(
        "Service booked successfully! The provider will contact you soon.",
        "success"
      );
    } catch (error) {
      
      showSnackbar("Failed to book service", "error");
    }
  };

  // Make phone call to service provider
  const makePhoneCall = useCallback(
    async (service) => {
      try {
        setSelectedService(service);
        setRatingDialogOpen(true);

        // Log call data to API
        await axios.post(
          `${url.baseAPIUrl}/api/ServiceLikeReviewCall/CallDetails`,
          null,
          {
            params: {
              Name: encodeURIComponent(service.fullName || "N/A"),
              BusinessName: encodeURIComponent(service.subcategory || "N/A"),
              Email: "N/A",
              ProviderMobile: encodeURIComponent(service.ProviderMobile),
              CustomerMobile: encodeURIComponent(currentUser || "N/A"),
            },
          }
        );

        // Redirect to phone dialer
        window.location.href = `tel:${service.ProviderMobile}`;
      } catch (error) {
        showSnackbar("Failed to initiate call", "error");
      }
    },
    [currentUser]
  );

  // Launch WhatsApp chat
  const launchWhatsApp = useCallback((phoneNumber) => {
    if (!phoneNumber) {
      return;
    }

    const cleanedNumber = phoneNumber.replace(/[^\d+]/g, "");
    const message = "Hello! I'd like to chat about your services.";
    const encodedMessage = encodeURIComponent(message);

    window.open(
      `https://wa.me/${cleanedNumber}?text=${encodedMessage}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, []);

  // Render service card component
  const renderServiceCard = useCallback(
    (service) => (
      <motion.div
        key={service.ProviderMobile}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{ scale: 1.02 }}
        className="service-search-card-container"
      >
        <Card className="service-search-card">
          <div className="service-search-card-content">
            <div className="service-search-image-container">
              <Suspense
                fallback={
                  <div className="service-search-carousel-loading">
                    Loading...
                  </div>
                }
              >
                <Carousel
                  showArrows={true}
                  showThumbs={false}
                  infiniteLoop={true}
                  autoPlay={true}
                  interval={3000}
                  stopOnHover={true}
                  dynamicHeight={false}
                  emulateTouch={true}
                  renderIndicator={(
                    onClickHandler,
                    isSelected,
                    index,
                    label
                  ) => (
                    <li
                      className={`service-search-carousel-indicator ${
                        isSelected ? "service-search-selected" : ""
                      }`}
                      onClick={onClickHandler}
                      onKeyDown={onClickHandler}
                      key={index}
                      role="button"
                      tabIndex={0}
                      aria-label={`${label} ${index + 1}`}
                    />
                  )}
                >
                  {service.AllImages?.split(",").map((image, idx) => (
                    <div key={idx} className="service-search-carousel-item">
                      <img
                        loading="lazy"
                        src={`${url.serviceImageStartingPath}${image}`}
                        alt={service.BusinessName}
                        className="service-search-carousel-image"
                      />
                    </div>
                  ))}
                </Carousel>
              </Suspense>
            </div>

            <div className="service-search-details">
              <div className="service-search-header">
                <Typography
                  variant="h6"
                  className="service-search-title"
                  onClick={() => handleServiceClick(service)}
                >
                  {service.BusinessName}
                </Typography>
                <div className="service-search-rating">
                  <Rating
                    value={service.rating}
                    precision={0.5}
                    readOnly
                    className="service-search-rating-stars"
                  />
                  <span className="service-search-rating-text">
                    {service.rating.toFixed(1)} ({service.reviews.length}{" "}
                    reviews)
                  </span>
                </div>
              </div>

              <div className="service-search-address">
                <FiMapPin className="service-search-address-icon" />
                <Typography
                  variant="body2"
                  className="service-search-address-text"
                >
                  {service.Address || "Address not available"}
                </Typography>
              </div>

              <div className="service-search-offered-services">
                <Typography
                  variant="subtitle2"
                  className="service-search-offered-services-title"
                >
                  Offered Services:
                </Typography>
                <div className="service-search-chips-container">
                  {service.subCategories?.slice(0, 3).map((subCat, idx) => (
                    <Chip
                      key={idx}
                      label={
                        <div className="service-search-chip-content">
                          <span className="service-search-chip-name">
                            {subCat.serviceName}
                          </span>
                          {subCat.pricePerAssign ? (
                            <span className="service-search-chip-price">
                              <Phone fontSize="inherit" /> Call for price
                            </span>
                          ) : (
                            <span className="service-search-chip-price">
                              Rs. {subCat.mrp}
                            </span>
                          )}
                        </div>
                      }
                      className={`service-search-chip ${
                        subCat.pricePerAssign
                          ? "service-search-call-price-chip"
                          : "service-search-fixed-price-chip"
                      }`}
                      onClick={() =>
                        subCat.pricePerAssign
                          ? makePhoneCall(service)
                          : openBookingDialog(service)
                      }
                      avatar={
                        <Avatar className="service-search-chip-badge">
                          {subCat.pricePerAssign ? "Call" : "Fixed"}
                        </Avatar>
                      }
                    />
                  ))}
                </div>
              </div>

              <div className="service-search-actions">
                <Button
                  variant="contained"
                  color="primary"
                  className="service-search-action-button service-search-book-button"
                  startIcon={<BookOnline />}
                  onClick={() => openBookingDialog(service)}
                >
                  Book Now
                </Button>
                <div className="service-search-secondary-actions">
                  <IconButton
                    className="service-search-action-icon service-search-whatsapp-button"
                    onClick={() => launchWhatsApp(service.ProviderMobile)}
                  >
                    <Chat />
                  </IconButton>
                  <IconButton
                    className="service-search-action-icon service-search-call-button"
                    onClick={() => makePhoneCall(service)}
                  >
                    <Phone />
                  </IconButton>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    ),
    [handleServiceClick, launchWhatsApp, makePhoneCall]
  );

  // Render loading skeleton
  const renderLoadingSkeleton = useCallback(
    () => (
      <div className="service-search-skeleton-container">
        {[...Array(5)].map((_, idx) => (
          <Card className="service-search-skeleton" key={idx}>
            <div className="service-search-skeleton-image" />
            <div className="service-search-skeleton-content">
              <div className="service-search-skeleton-title" />
              <div className="service-search-skeleton-rating" />
              <div className="service-search-skeleton-address" />
              <div className="service-search-skeleton-chips">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="service-search-skeleton-chip" />
                ))}
              </div>
              <div className="service-search-skeleton-actions">
                <div className="service-search-skeleton-primary-action" />
                <div className="service-search-skeleton-secondary-actions">
                  {[...Array(2)].map((_, i) => (
                    <div
                      key={i}
                      className="service-search-skeleton-secondary-action"
                    />
                  ))}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    ),
    []
  );

  return (
    <>
      <Helmet>
        <title>
          {decodedSearchTerm
            ? `"${decodedSearchTerm}" Services | Search Results`
            : "Find Services | Search"}
        </title>
        <meta
          name="description"
          content={
            decodedSearchTerm
              ? `Find and book ${decodedSearchTerm.toLowerCase()} services near you. ${
                  filteredData.length
                } providers available.`
              : "Search and book professional services in your area."
          }
        />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={
            decodedSearchTerm
              ? `${decodedSearchTerm} Services | Search Results`
              : "Find Services | Search"
          }
        />
        <meta
          property="og:description"
          content={
            decodedSearchTerm
              ? `Browse ${
                  filteredData.length
                } ${decodedSearchTerm.toLowerCase()} service providers near you.`
              : "Discover and book professional services in your area."
          }
        />
        <meta
          property="og:image"
          content={
            filteredData.length > 0 && filteredData[0].AllImages
              ? `${url.serviceImageStartingPath}${
                  filteredData[0].AllImages.split(",")[0]
                }`
              : "/images/default-service-search.jpg"
          }
        />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Header
        hasShowOptions={true}
        userLocation={userLocation}
        setShowModal={setShowModal}
        setUserLocation={updateLocation}
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <Container
        maxWidth="xl"
        className="service-search-main-container"
        ref={scrollRef}
      >
        <Box className="service-search-page-header">
          <Typography variant="h4" className="service-search-page-title">
            {decodedSearchTerm
              ? `Search Results for "${decodedSearchTerm}"`
              : "Search Services"}
          </Typography>
          <Typography
            variant="subtitle1"
            className="service-search-results-count"
          >
            {filteredData.length} services found
          </Typography>
        </Box>

        {isLoading || isLocationLoading ? (
          renderLoadingSkeleton()
        ) : filteredData.length > 0 ? (
          <div className="service-search-services-grid">
            {filteredData.map((service) => renderServiceCard(service))}
          </div>
        ) : (
          <Box className="service-search-empty-state">
            <img
              src="/images/no-services-found.png"
              alt="No services found"
              className="service-search-empty-image"
            />
            <Typography variant="h5" className="service-search-empty-title">
              No Services Found
            </Typography>
            <Typography
              variant="body1"
              className="service-search-empty-message"
            >
              {decodedSearchTerm
                ? `We couldn't find any services matching "${decodedSearchTerm}". Try a different term.`
                : "Enter a search term to find services"}
            </Typography>
          </Box>
        )}

        {/* Booking Dialog */}
        <Dialog
          open={bookingDialogOpen}
          onClose={() => setBookingDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          className="service-search-booking-dialog"
        >
          <DialogTitle className="service-search-dialog-title">
            <div className="service-search-dialog-header">
              <Typography variant="h6">Book Services</Typography>
              <IconButton onClick={() => setBookingDialogOpen(false)}>
                <Close />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent dividers className="service-search-dialog-content">
            {selectedService && (
              <>
                <Typography
                  variant="subtitle1"
                  className="service-search-provider-name"
                >
                  {selectedService.BusinessName}
                </Typography>

                <div className="service-search-selected-services-list">
                  <Typography variant="subtitle2">
                    Services to be booked:
                  </Typography>
                  <ul>
                    {selectedServices.map((serviceName, idx) => {
                      const service = selectedService.subCategories.find(
                        (s) => s.serviceName === serviceName
                      );
                      return (
                        <li key={idx}>
                          {serviceName} - Rs. {service?.mrp || "0"}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Additional Details (Optional)"
                  placeholder="Describe your requirements..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="service-search-description-input"
                  variant="outlined"
                  margin="normal"
                />

                <div className="service-search-total-price">
                  <Typography variant="h6">
                    Total: Rs. {calculateTotalMRP()}
                  </Typography>
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions className="service-search-dialog-actions">
            <Button
              onClick={() => setBookingDialogOpen(false)}
              className="service-search-cancel-button"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={bookService}
              className="service-search-confirm-button"
              variant="contained"
              color="primary"
            >
              Confirm Booking
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          className="service-search-snackbar"
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
            className={`service-search-alert service-search-alert-${snackbar.severity}`}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
      <Footer />
    </>
  );
};

export default ServiceSearchScreen;
